import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';

function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { t } = useSettings();
  return (
    <div className="product-card" onClick={() => navigate(`/products/${product._id}`)}>
      {product.discount > 0 && <div style={{ position:'absolute', top:12, left:12, zIndex:2 }}><span className="badge badge-discount">-{product.discount}%</span></div>}
      <div className="product-card-img-wrap">
        <img src={product.thumbnail||'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400'} alt={product.name} className="product-card-image" />
        <div className="product-card-overlay">
          <button className="btn btn-primary btn-sm" onClick={e=>{e.stopPropagation();addItem(product);}}>{t('products','add_to_cart')}</button>
        </div>
      </div>
      <div className="product-card-body">
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
          <span className="product-card-brand">{product.brand}</span>
          <span style={{ fontSize:11, color:'var(--text-muted)', background:'var(--bg-secondary)', padding:'2px 8px', borderRadius:6 }}>{product.category}</span>
        </div>
        <h3 className="product-card-name">{product.name}</h3>
        <div className="product-card-rating">
          {[...Array(5)].map((_,i)=><span key={i} style={{ color: i<Math.round(product.rating||0)?'var(--gold)':'var(--border-light)' }}>★</span>)}
          <span>({product.numReviews||0} {t('products','reviews')})</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <span className="product-card-price">${product.price?.toLocaleString()}</span>
            {product.originalPrice>product.price && (
              <>
                <span className="product-card-original">${product.originalPrice?.toLocaleString()}</span>
                <span style={{ fontSize: 11, background: 'rgba(34,197,94,0.12)', color: 'var(--success)', padding: '2px 8px', borderRadius: 10, fontWeight: 700, marginLeft: 4 }}>
                  -{product.discount}%
                </span>
              </>
            )}
          </div>
          <button className="btn btn-primary btn-sm" onClick={e=>{e.stopPropagation();addItem(product);}}>{t('products','add')}</button>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [searchParams] = useSearchParams();
  const { t } = useSettings();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [page, setPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: searchParams.get('search')||'',
    category: searchParams.get('category')||'',
    brand: '', minPrice: '', maxPrice: '', sort: 'newest',
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, limit: 12 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await productsAPI.getAll(params);
      setProducts(res.data.products); setTotal(res.data.total);
    } catch {}
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { productsAPI.getCategories().then(r => { setCategories(r.data.categories); setBrands(r.data.brands); }).catch(()=>{}); }, []);

  const upd = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };
  const clearFilters = () => { setFilters({ search:'', category:'', brand:'', minPrice:'', maxPrice:'', sort:'newest' }); setPage(1); };
  const activeCount = Object.entries(filters).filter(([k,v]) => v && k !== 'sort' && k !== 'search').length;

  return (
    <div className="container" style={{ padding:'40px 28px' }}>
      {/* Mobile filter toggle */}
      <button className="products-page-sidebar-toggle btn btn-secondary" onClick={() => setSidebarOpen(true)} style={{ gap:8 }}>
        <SlidersHorizontal size={16} /> {t('products','filters')}{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>
      {/* Sidebar overlay (mobile) */}
      <div className={`products-sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <div className="products-layout" style={{ display:'flex', gap:28 }}>
        {/* Sidebar */}
        <div className={`products-sidebar card ${sidebarOpen ? 'open' : ''}`} style={{ width:256, flexShrink:0, background:'var(--bg-card)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
            <h3 style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
              <SlidersHorizontal size={16} color="var(--accent)" /> {t('products','filters')}
            </h3>
            <div style={{ display:'flex', gap:6 }}>
              {activeCount > 0 && <button style={{ color:'var(--danger)', background:'none', border:'none', cursor:'pointer', fontSize:13, fontWeight:600 }} onClick={clearFilters}>Clear ({activeCount})</button>}
              <button className="btn btn-ghost btn-icon btn-sm products-sidebar-close" onClick={() => setSidebarOpen(false)}><X size={16} /></button>
            </div>
          </div>
            <div className="form-group">
              <label className="form-label">{t('products','category')}</label>
              <select className="form-input" value={filters.category} onChange={e => upd('category', e.target.value)}>
                <option value="">{t('products','all_categories')}</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('products','brand')}</label>
              <select className="form-input" value={filters.brand} onChange={e => upd('brand', e.target.value)}>
                <option value="">{t('products','all_brands')}</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('products','price_range')}</label>
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-input" placeholder="Min" type="number" value={filters.minPrice} onChange={e => upd('minPrice', e.target.value)} style={{ width:'50%' }} />
                <input className="form-input" placeholder="Max" type="number" value={filters.maxPrice} onChange={e => upd('maxPrice', e.target.value)} style={{ width:'50%' }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('products','sort_by')}</label>
              <select className="form-input" value={filters.sort} onChange={e => upd('sort', e.target.value)}>
                <option value="newest">{t('products','newest')}</option>
                <option value="price-asc">{t('products','price_asc')}</option>
                <option value="price-desc">{t('products','price_desc')}</option>
                <option value="rating">{t('products','best_rated')}</option>
                <option value="popular">{t('products','popular')}</option>
              </select>
            </div>
        </div>
        {/* Main */}
        <div style={{ flex:1 }}>
          <div style={{ position:'relative', marginBottom:20 }}>
            <Search size={18} style={{ position:'absolute', left:16, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
            <input className="form-input" placeholder={t('nav','search')} value={filters.search} onChange={e => upd('search', e.target.value)} style={{ paddingInlineStart:48 }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20, color:'var(--text-secondary)', fontSize:14 }}>
            <span>{total} {t('products','found')}</span>
            {filters.category && <span style={{ color:'var(--accent)' }}>in {filters.category}</span>}
          </div>
          {loading ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:24 }}>
              {[...Array(6)].map((_,i) => <div key={i} className="skeleton" style={{ height:380, borderRadius:20 }} />)}
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign:'center', padding:80, color:'var(--text-muted)' }}>
              <div style={{ fontSize:56, marginBottom:20 }}>🔍</div>
              <p style={{ fontSize:18, marginBottom:16 }}>{t('products','no_results')}</p>
              <button className="btn btn-secondary" onClick={clearFilters}>{t('products','clear_filters')}</button>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {products.map(p => <ProductCard key={p._id} product={p} />)}
              </div>
              {total > 12 && (
                <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:40 }}>
                  {[...Array(Math.ceil(total/12))].map((_,i) => (
                    <button key={i} onClick={() => setPage(i+1)} className={`btn btn-sm ${page===i+1?'btn-primary':'btn-secondary'}`}>{i+1}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}